import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { signOut } from "../api/auth.js";
import { setActiveOrganisationHeader } from "../api/client.js";
import { ApiError, displayError } from "../api/errors.js";
import { planSignOut } from "./people-on-computer.js";
import {
  clearPeople,
  removePerson,
  usePeopleOnComputer,
} from "./people-store.js";
import {
  lockSharedDevice,
  removeCurrentDeviceAccount,
  unbindSharedDevice,
} from "./quick-access-api.js";
import { clearAllDrafts, clearDraft } from "../logging/draft-store";

function ignoreStatus(error: unknown, statuses: readonly number[]): void {
  if (error instanceof ApiError && statuses.includes(error.status)) return;
  throw error;
}

export interface ComputerActions {
  addPerson: () => Promise<void>;
  error: string | null;
  lockForIdle: () => Promise<void>;
  peopleCount: number;
  pending: boolean;
  signOutEveryone: () => Promise<void>;
  signOutOfComputer: () => Promise<void>;
  switchPerson: () => Promise<void>;
}

export function useComputerActions(person: {
  authMethod: "primary" | "shared_pin";
  staffProfileIds: readonly string[];
  userId: string;
}): ComputerActions {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const people = usePeopleOnComputer();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(work: () => Promise<"/lock" | "/sign-in">) {
    setPending(true);
    setError(null);
    try {
      const destination = await work();
      setActiveOrganisationHeader(null);
      queryClient.clear();
      await navigate({ to: destination });
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  async function endSession() {
    if (person.authMethod === "shared_pin") {
      try {
        await lockSharedDevice();
      } catch (caught) {
        ignoreStatus(caught, [401]);
      }
      return;
    }
    try {
      await signOut();
    } catch (caught) {
      ignoreStatus(caught, [401]);
    }
  }

  return {
    addPerson: () =>
      run(async () => {
        await lockSharedDevice();
        return "/sign-in";
      }),
    error,
    lockForIdle: () =>
      run(async () => {
        await lockSharedDevice().catch(() => undefined);
        return "/lock";
      }),
    peopleCount: people.tiles.length,
    pending,
    signOutEveryone: () =>
      run(async () => {
        await unbindSharedDevice();
        await endSession().catch(() => undefined);
        clearAllDrafts();
        if (people.deviceId) clearPeople(people.deviceId);
        return "/sign-in";
      }),
    signOutOfComputer: () =>
      run(async () => {
        const plan = planSignOut(
          people.tiles,
          person.userId,
          person.staffProfileIds,
        );
        try {
          await removeCurrentDeviceAccount();
        } catch (caught) {
          ignoreStatus(caught, [401, 403, 404]);
        }
        await endSession();
        clearDraft(person.userId);
        if (people.deviceId) removePerson(people.deviceId, person.userId);
        if (plan.unbind) {
          await unbindSharedDevice().catch(() => undefined);
          if (people.deviceId) clearPeople(people.deviceId);
        }
        return plan.destination;
      }),
    switchPerson: () =>
      run(async () => {
        await lockSharedDevice();
        return "/lock";
      }),
  };
}
